import { apiClient } from '../client'
import type { RoleComponentGrant, SaveRoleComponentsResponse } from '../../app/modules/types/componentAccess'
import { OWNS_COMPONENT_REFUSAL } from '../ownsErrors'
import type {
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  CreateSubOrganizationRequest,
  SetDefaultOrganizationRequest,
  OrganizationMembership,
  AddMemberRequest,
  CreateUserAndAddToOrgRequest,
  UpdateMemberRoleRequest,
  OrganizationRole,
  CreateRoleRequest,
  UpdateRoleRequest,
  RolePermissionsResponse,
  GrantPermissionsRequest,
  RevokePermissionsRequest,
  ObjectPermissionsResponse,
  GrantObjectPermissionRequest,
  RevokeObjectPermissionRequest,
  SetOrganizationLimitRequest,
  SetOrganizationLimitResponse,
  OrganizationUsageResponse,
  SetUserLimitRequest,
  SetUserLimitResponse,
  UserLimitsListResponse,
  UserUsageResponse,
  ResourceType,
  OrganizationIpRange,
  BatchCreateIpRangeRequest,
  BatchCreateIpRangeResponse,
  BatchUpdateIpRangeRequest,
  BatchUpdateIpRangeResponse,
  ParentIpRangesResponse,
} from '../../app/modules/accounts/components/organization/types'

/**
 * Organizations API endpoints
 */
export const organizationsApi = {
  // ============================================
  // ORGANIZATIONS
  // ============================================

  /**
   * Get all organizations the authenticated user has access to
   */
  getAll: async (): Promise<Organization[]> => {
    const { data } = await apiClient.get('/organizations/')
    return data
  },

  /**
   * Get a specific organization by ID
   */
  getById: async (organizationId: string): Promise<Organization> => {
    const { data } = await apiClient.get(`/organizations/${organizationId}/`)
    return data
  },

  /**
   * Get the user's current default organization
   */
  getCurrent: async (): Promise<Organization> => {
    const { data } = await apiClient.get('/organizations/current/')
    return data
  },

  /**
   * Create a new root organization
   */
  create: async (payload: CreateOrganizationRequest): Promise<Organization> => {
    const { data } = await apiClient.post('/organizations/setup/', payload)
    return data
  },

  /**
   * Update organization details
   */
  update: async (organizationId: string, payload: UpdateOrganizationRequest): Promise<Organization> => {
    const { data } = await apiClient.patch(`/organizations/${organizationId}/`, payload)
    return data
  },

  /**
   * Delete an organization and all its descendants
   */
  delete: async (organizationId: string): Promise<void> => {
    await apiClient.delete(`/organizations/${organizationId}/`)
  },

  /**
   * Create a child organization under an existing organization
   */
  createSubOrganization: async (
    parentOrganizationId: string,
    payload: CreateSubOrganizationRequest
  ): Promise<Organization> => {
    const { data } = await apiClient.post(
      `/organizations/${parentOrganizationId}/add-sub-organization/`,
      payload
    )
    return data
  },

  /**
   * Set the user's default/active organization
   */
  setDefault: async (payload: SetDefaultOrganizationRequest): Promise<OrganizationMembership> => {
    const { data } = await apiClient.post('/organizations/set-default/', payload)
    return data
  },

  // ============================================
  // IP RANGES
  // ============================================

  /**
   * Get all IP ranges for an organization
   */
  getIpRanges: async (orgId: string): Promise<OrganizationIpRange[]> => {
    const { data } = await apiClient.get(`/organizations/${orgId}/ip-ranges/`)
    return data
  },

  /**
   * Get a single IP range by ID
   */
  getIpRange: async (orgId: string, ipRangeId: string): Promise<OrganizationIpRange> => {
    const { data } = await apiClient.get(`/organizations/${orgId}/ip-ranges/${ipRangeId}/`)
    return data
  },

  /**
   * Get parent organization's IP ranges
   */
  getParentIpRanges: async (orgId: string): Promise<ParentIpRangesResponse> => {
    const { data } = await apiClient.get(`/organizations/${orgId}/ip-ranges/parent-ranges/`)
    return data
  },

  /**
   * Batch create IP ranges
   */
  batchCreateIpRanges: async (
    orgId: string,
    payload: BatchCreateIpRangeRequest
  ): Promise<BatchCreateIpRangeResponse> => {
    const { data } = await apiClient.post(`/organizations/${orgId}/ip-ranges/batch-create/`, payload)
    return data
  },

  /**
   * Batch update IP ranges (replaces all IP ranges atomically)
   */
  batchUpdateIpRanges: async (
    orgId: string,
    payload: BatchUpdateIpRangeRequest
  ): Promise<BatchUpdateIpRangeResponse> => {
    const { data } = await apiClient.put(`/organizations/${orgId}/ip-ranges/batch-update/`, payload)
    return data
  },

  /**
   * Delete a single IP range
   */
  deleteIpRange: async (orgId: string, ipRangeId: string): Promise<void> => {
    await apiClient.delete(`/organizations/${orgId}/ip-ranges/${ipRangeId}/`)
  },

  // ============================================
  // MEMBERSHIPS
  // ============================================

  /**
   * Get all members of an organization
   */
  getMembers: async (organizationId: string, includeDescendants?: boolean): Promise<OrganizationMembership[]> => {
    const params = includeDescendants ? { include_descendants: true } : {}
    const { data } = await apiClient.get(`/organizations/${organizationId}/members/`, { params })
    return data
  },

  /**
   * Get details of a specific membership
   */
  getMember: async (organizationId: string, membershipId: string): Promise<OrganizationMembership> => {
    const { data } = await apiClient.get(`/organizations/${organizationId}/members/${membershipId}/`)
    return data
  },

  /**
   * Add a user to an organization with a specific role
   */
  addMember: async (organizationId: string, payload: AddMemberRequest) => {
    const { data } = await apiClient.post(`/organizations/${organizationId}/members/`, payload)
    return data
  },

  /**
   * Update a member's role in the organization
   */
  updateMemberRole: async (
    organizationId: string,
    membershipId: string,
    payload: UpdateMemberRoleRequest
  ): Promise<OrganizationMembership> => {
    const { data } = await apiClient.patch(
      `/organizations/${organizationId}/members/${membershipId}/`,
      payload
    )
    return data
  },

  /**
   * Remove a user from an organization
   */
  removeMember: async (organizationId: string, membershipId: string): Promise<void> => {
    await apiClient.delete(`/organizations/${organizationId}/members/${membershipId}/`)
  },

  /**
   * Create a new user and add them to the organization in a single atomic operation
   */
  createUserAndAddToOrg: async (
    organizationId: string,
    payload: CreateUserAndAddToOrgRequest
  ): Promise<OrganizationMembership> => {
    const { data } = await apiClient.post(
      `/organizations/${organizationId}/members/create-and-add/`,
      payload
    )
    return data
  },

  // ============================================
  // ROLES
  // ============================================

  /**
   * Get all roles for an organization
   */
  getRoles: async (organizationId: string, params?: Record<string, any>): Promise<OrganizationRole[]> => {
    const { data } = await apiClient.get(`/organizations/${organizationId}/roles/`, { params })
    return data
  },

  /**
   * Get a specific role by ID
   */
  getRole: async (organizationId: string, roleId: string): Promise<OrganizationRole> => {
    const { data } = await apiClient.get(`/organizations/${organizationId}/roles/${roleId}/`)
    return data
  },

  /**
   * Get members assigned to a specific role
   */
  getRoleMembers: async (organizationId: string, roleId: string): Promise<OrganizationMembership[]> => {
    const { data } = await apiClient.get(`/organizations/${organizationId}/roles/${roleId}/members/`)
    return data
  },

  /**
   * Create a new role in the organization
   */
  createRole: async (organizationId: string, payload: CreateRoleRequest): Promise<OrganizationRole> => {
    const { data } = await apiClient.post(`/organizations/${organizationId}/roles/`, payload)
    return data
  },

  /**
   * Update role details
   */
  updateRole: async (
    organizationId: string,
    roleId: string,
    payload: UpdateRoleRequest
  ): Promise<OrganizationRole> => {
    const { data } = await apiClient.patch(`/organizations/${organizationId}/roles/${roleId}/`, payload)
    return data
  },

  /**
   * Delete a role from the organization
   */
  deleteRole: async (organizationId: string, roleId: string): Promise<void> => {
    await apiClient.delete(`/organizations/${organizationId}/roles/${roleId}/`)
  },

  // ============================================
  // PERMISSIONS
  // ============================================

  /**
   * Get role permissions with grant status (available permissions for the role)
   */
  getRolePermissions: async (organizationId: string, roleId: string): Promise<RolePermissionsResponse> => {
    const { data } = await apiClient.get(
      `/organizations/${organizationId}/permissions/roles/${roleId}/available/`
    )
    return data
  },

  /**
   * Get object permissions for a role with access status
   */
  getObjectPermissions: async (
    organizationId: string,
    roleId: string,
    resourceType: string
  ): Promise<ObjectPermissionsResponse> => {
    const { data } = await apiClient.get(
      `/organizations/${organizationId}/permissions/roles/${roleId}/objects/${resourceType}/available/`
    )
    return data
  },

  /**
   * Save a role's COMPLETE component set in one call (not a diff). An empty list
   * removes every per-component grant but NOT the all-components permission.
   * 403s here carry only `detail` and are rendered by the role screen itself.
   */
  saveRoleComponents: async (
    organizationId: string,
    roleId: string,
    grants: RoleComponentGrant[],
  ): Promise<SaveRoleComponentsResponse> => {
    const { data } = await apiClient.put(
      `/organizations/${organizationId}/permissions/roles/${roleId}/objects/component/`,
      { grants },
      OWNS_COMPONENT_REFUSAL,
    )
    return data
  },

  /**
   * Get available object resource types for an organization
   */
  getObjectResourceTypes: async (
    organizationId: string
  ): Promise<Record<string, Array<{ resource_type: string; name: string }>>> => {
    const { data } = await apiClient.get(
      `/organizations/${organizationId}/permissions/object-resource-types/`
    )
    return data
  },

  /**
   * Get object permissions list for a resource type with optional role filter
   */
  getObjectPermissionsList: async (
    organizationId: string,
    resourceType: string,
    roleId: string,
    params?: Record<string, any>
  ): Promise<any> => {
    const { data } = await apiClient.get(
      `/organizations/${organizationId}/permissions/roles/${roleId}/objects/${resourceType}/available/`,
      // { params } https://stg-api.securest.ir/api/v1/organizations/01KFWSAEVCPZ85K9PFN3YSXNDS/permissions/roles/01KHNCQMWH367BMDNYEHP59BTM/objects/ip/available/
    )
    return data
  },

  /**
   * Create/update object permission
   */
  createObjectPermission: async (
    organizationId: string,
    resourceType: string,
    objectId: string,
    payload: any
  ): Promise<any> => {
    const { data } = await apiClient.post(
      `/organizations/${organizationId}/permissions/objects/${resourceType}/${objectId}/grant/`,
      payload
    )
    return data
  },

  /**
   * Delete object permission
   */
  deleteObjectPermission: async (
    organizationId: string,
    resourceType: string,
    objectId: string,
    payload: any
  ): Promise<void> => {
    await apiClient.post(
      // /organizations/{i}/permissions/objects/{i}/{i}/grant/
      `/organizations/${organizationId}/permissions/objects/${resourceType}/${objectId}/revoke/`,
      payload
    )
  },

  /**
   * Grant API permissions to a role
   */
  grantPermissions: async (
    organizationId: string,
    roleId: string,
    payload: GrantPermissionsRequest
  ): Promise<{ message: string }> => {
    const { data } = await apiClient.post(
      `/organizations/${organizationId}/permissions/roles/${roleId}/grant/`,
      payload
    )
    return data
  },

  /**
   * Revoke API permissions from a role
   */
  revokePermissions: async (
    organizationId: string,
    roleId: string,
    payload: RevokePermissionsRequest
  ): Promise<{ message: string }> => {
    const { data } = await apiClient.post(
      `/organizations/${organizationId}/permissions/roles/${roleId}/revoke/`,
      payload
    )
    return data
  },

  /**
   * Grant object permission to a role for a specific object
   */
  grantObjectPermission: async (
    organizationId: string,
    resourceType: string,
    objectId: string,
    payload: GrantObjectPermissionRequest
  ): Promise<{ message: string }> => {
    const { data } = await apiClient.post(
      `/organizations/${organizationId}/permissions/objects/${resourceType}/${objectId}/grant/`,
      payload
    )
    return data
  },

  /**
   * Revoke object permission from a role for a specific object
   */
  revokeObjectPermission: async (
    organizationId: string,
    resourceType: string,
    objectId: string,
    payload: RevokeObjectPermissionRequest
  ): Promise<{ message: string }> => {
    const { data } = await apiClient.post(
      `/organizations/${organizationId}/permissions/objects/${resourceType}/${objectId}/revoke/`,
      payload
    )
    return data
  },

  // ============================================
  // LIMITS & USAGE
  // ============================================

  /**
   * Get resource usage for an organization
   */
  getOrganizationUsage: async (
    organizationId: string,
    resourceType: ResourceType
  ): Promise<OrganizationUsageResponse> => {
    const { data } = await apiClient.get(`/organizations/${organizationId}/organization-limits/usage/`, {
      params: { resource_type: resourceType },
    })
    return data
  },

  /**
   * Set organization-level resource limits
   */
  setOrganizationLimit: async (
    organizationId: string,
    payload: SetOrganizationLimitRequest
  ): Promise<SetOrganizationLimitResponse> => {
    const { data } = await apiClient.post(
      `/organizations/${organizationId}/organization-limits/set/`,
      payload
    )
    return data
  },

  /**
   * Get all user-level limits within an organization
   */
  getUserLimits: async (organizationId: string, userId?: string): Promise<UserLimitsListResponse> => {
    const params = userId ? { user_id: userId } : {}
    const { data } = await apiClient.get(`/organizations/${organizationId}/user-limits/list/`, { params })
    return data
  },

  /**
   * Set user-level resource limits within an organization
   */
  setUserLimit: async (
    organizationId: string,
    payload: SetUserLimitRequest
  ): Promise<SetUserLimitResponse> => {
    const { data } = await apiClient.post(`/organizations/${organizationId}/user-limits/set/`, payload)
    return data
  },

  /**
   * Get resource usage for a specific user within an organization
   */
  getUserUsage: async (
    organizationId: string,
    resourceType: ResourceType,
    userId?: string
  ): Promise<UserUsageResponse> => {
    const params: { resource_type: ResourceType; user_id?: string } = {
      resource_type: resourceType,
    }
    if (userId) {
      params.user_id = userId
    }
    const { data } = await apiClient.get(`/organizations/${organizationId}/user-limits/usage/`, { params })
    return data
  },
}
